<script setup lang="ts">
import type { InterestControlDescriptor } from '~/utils/event-policy';
import type { InterestRung } from '@@/types/api';

const props = defineProps<{
  descriptor: InterestControlDescriptor;
  eventId: string;
  pending?: boolean;
}>();

const emit = defineEmits<{
  select: [rung: InterestRung | null];
}>();

const route = useRoute();

/** Almanac variant offers the "going" rung; native is "interested"-only. */
const isAlmanac = computed(() => props.descriptor.options.includes('going'));

const isDisabled = computed(
  () => props.pending === true || props.descriptor.disabled,
);

/**
 * Build the `/login?redirect_to=...` URL, riding the interest marker INSIDE the
 * redirect target so it survives the SSO round-trip. The later resume composable
 * reads `route.query.interest` on return (native `1`, almanac `interested`/`going`),
 * mirroring the `?commit=1` convention in `use-commit-resume.ts`.
 */
function loginUrl(markerValue: string): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(route.query)) {
    if (typeof value === 'string') params.set(key, value);
  }
  params.set('interest', markerValue);
  const qs = params.toString();
  const target = qs ? `${route.path}?${qs}` : route.path;
  return `/login?redirect_to=${encodeURIComponent(target)}`;
}

function onNativeClick() {
  if (isDisabled.value) return;
  if (props.descriptor.action === 'login') {
    navigateTo(loginUrl('1'));
    return;
  }
  // Toggle: turn off if currently interested, on otherwise.
  emit('select', props.descriptor.rung ? null : 'interested');
}

function onToggle(value: unknown) {
  if (isDisabled.value) return;
  const next = (value ?? null) as InterestRung | null;
  if (props.descriptor.action === 'login') {
    // Visitors have no current rung — a click always selects a rung.
    if (next) navigateTo(loginUrl(next));
    return;
  }
  emit('select', next);
}
</script>

<template>
  <div
    v-if="descriptor.visible"
    class="event-interest-control"
    :title="descriptor.disabledReason ?? undefined"
  >
    <!-- Almanac variant: deselectable Interested / Going toggle -->
    <v-btn-toggle
      v-if="isAlmanac"
      :model-value="descriptor.rung"
      color="primary"
      variant="outlined"
      divided
      density="comfortable"
      :disabled="isDisabled"
      @update:model-value="onToggle"
    >
      <v-btn
        value="interested"
        prepend-icon="mdi-heart-outline"
        aria-label="Interested"
      >
        Interested
      </v-btn>
      <v-btn
        value="going"
        prepend-icon="mdi-check-circle-outline"
        aria-label="Going"
      >
        Going
      </v-btn>
    </v-btn-toggle>

    <!-- Native variant: single Interested toggle -->
    <v-btn
      v-else
      rounded
      aria-label="Interested"
      :variant="descriptor.rung === 'interested' ? 'flat' : 'outlined'"
      :color="descriptor.rung === 'interested' ? 'primary' : undefined"
      :prepend-icon="
        descriptor.rung === 'interested' ? 'mdi-heart' : 'mdi-heart-outline'
      "
      :disabled="isDisabled"
      @click="onNativeClick"
    >
      Interested
    </v-btn>
  </div>
</template>
